import { Footer } from "@/components/LandingPage/Footer";
import { GetStarted } from "@/components/LandingPage/GetStarted";
import Navbar from "@/components/LandingPage/Navbar";
import {
  Box,
  Button,
  Center,
  Container,
  Flex,
  Heading,
  HStack,
  Stack,
  Text,
} from "@chakra-ui/react";
import { GetStaticProps } from "next";
import { useTranslations } from "next-intl";
import Head from "next/head";
import Image from "next/image";
import type { Metadata } from "next";
import { TestimonialsSection } from "@/components/LandingPage/TestimonialsSection";
import { useViewport } from "@/hooks/useViewport";
import heroBg from "@root/public/images/photos/clarifayo_learn.jpg";
import clarifayoCourses from "@root/public/images/photos/clarifayo_courses.png";
import clarifayoTools from "@root/public/images/photos/clarifayo_tools.png";
import clarifayoAssessments from "@root/public/images/photos/clarifayo_assessments.png";
import Link from "next/link";
import { LetsClarifayoLink } from "@/components/LandingPage/LetsClarifayoLink";

export const metadata: Metadata = {
  title: "Clarifayo",
  description: "Unlock your potential with the ultimate coaching experience!",
};

const Hero = () => {
  const t = useTranslations();
  const { isMobile } = useViewport();

  return (
    <Stack
      gap={{ base: 8, md: 14 }}
      py={{ base: 8, md: 20 }}
      px={{
        base: 8,
        md: 20,
      }}
      justifyContent={"flex-end"}
      bgColor="brand.1000"
      h={{
        base: "90vh",
        md: "90vh",
      }}
      position="relative"
      overflow={"hidden"}
      backgroundImage={`url(${heroBg.src})`}
      backgroundSize="cover"
      backgroundPosition="center"
      backgroundAttachment="fixed"
    >
      <Box className="learning-hero-bg">
        {/* {isMobile ? (
          <Image
            src={heroBg}
            alt="hero background"
            width={4352 / 4}
            height={2901 / 4}
            quality={80}
            objectFit="cover"
            loading="eager"
            style={{
              position: "absolute",
              objectFit: "cover",
              left: 0,
              top: 0,
              height: "100%",
              zIndex: 0,
            }}
          />
        ) : (
          <Image
            src={heroBg}
            alt="hero background"
            width={4352 / 4}
            height={2901 / 4}
            quality={80}
            objectFit="cover"
            loading="eager"
            style={{
              position: "absolute",
              aspectRatio: "16/9",
              left: 0,
              top: 0,
              maxWidth: "100vw",
              width: "100vw",
              height: "100vh",
              objectFit: "cover",
              zIndex: 0,
            }}
          />
        )} */}
      </Box>
      <Box position="absolute" top={0} left={0} w="full">
        <Navbar />
      </Box>

      <Stack
        direction={["column", "row"]}
        gap={16}
        alignItems={"flex-end"}
        w="full"
        zIndex={1}
      >
        <Box flex={1} maxW={{ base: "full", md: "50%" }}>
          <Heading
            fontWeight={600}
            fontSize={{ base: "42px", md: "64px" }}
            mb={4}
            textAlign={{ base: "center", md: "left" }}
            color="white"
            lineHeight={"1.1"}
            letterSpacing={".1rem"}
            fontFamily={`'Marlide', sans-serif`}
          >
            <Text
              dangerouslySetInnerHTML={{ __html: t.raw("Learning.title") }}
            />
          </Heading>
          <Text
            fontSize={{ base: "18px", md: "22px" }}
            color="white"
            textAlign={{ base: "center", md: "left" }}
            maxW={{ base: "full", md: "600px" }}
            mb={8}
            dangerouslySetInnerHTML={{ __html: t.raw("Learning.subtitle") }}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

const CanaryContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box
      bgColor="brand.900"
      color="brand.1000"
      py={12}
      px={16}
      borderRadius={64}
      w="full"
      mt={16}
    >
      {children}
    </Box>
  );
};

const SectionHeader = ({
  title,
  description,
  image,
  reversed = false,
}: {
  title: string;
  description: string;
  image: any;
  reversed?: boolean;
}) => {
  return (
    <Stack
      direction={[
        reversed ? "column" : "column-reverse",
        reversed ? "row" : "row-reverse",
      ]}
      gap={8}
    >
      <Box flex={1}>
        <Heading
          mb={8}
          fontFamily={"Marlide"}
          fontSize={{ base: "24px", md: "35px" }}
          fontWeight={600}
          color="brand.1000"
        >
          {title}
        </Heading>
        <Text
          mb={8}
          fontSize={{ base: "18px", md: "22px" }}
          color="brand.1000"
          dangerouslySetInnerHTML={{ __html: description }}
        />
        <LetsClarifayoLink />
      </Box>
      <Center flex={1}>
        <Image src={image} alt={title} width={336} height={226} />
      </Center>
    </Stack>
  );
};

const CoursesSection = () => {
  const t = useTranslations();
  return (
    <Container
      maxW="container.xl"
      py={{ base: 8, md: 24 }}
      px={{ base: 8, md: 24 }}
      id="CoursesSection"
    >
      <SectionHeader
        title={t("Learning.courses.title")}
        description={t("Learning.courses.description")}
        image={clarifayoCourses}
      />
      {/* <CanaryContainer>
        <Box mb={16}>
          <Heading fontSize="3xl" textTransform={"uppercase"} mb={8}>
            {t("Learning.course1.title")}
          </Heading>
          <Text
            fontSize="lg"
            dangerouslySetInnerHTML={{
              __html: t("Learning.course1.description"),
            }}
          />
          
        </Box>
        <Box>
          <Heading fontSize="3xl" textTransform={"uppercase"} mb={8}>
            {t("Learning.course2.title")}
          </Heading>
          <Text
            fontSize="lg"
            dangerouslySetInnerHTML={{
              __html: t("Learning.course2.description"),
            }}
          />
          
        </Box>
      </CanaryContainer> */}
    </Container>
  );
};

const ToolsSection = () => {
  const t = useTranslations();
  return (
    <Container
      maxW="container.xl"
      py={{ base: 8, md: 24 }}
      px={{ base: 8, md: 24 }}
      id="ToolsSection"
    >
      <SectionHeader
        title={t("Learning.tools.title")}
        description={t("Learning.tools.description")}
        image={clarifayoTools}
        reversed
      />
      <CanaryContainer>
        <Box mb={16}>
          <Heading
            fontSize="3xl"
            textTransform={"uppercase"}
            mb={8}
            fontWeight="bold"
          >
            {t("Learning.tool1.title")}
          </Heading>
          <Text
            fontSize="lg"
            dangerouslySetInnerHTML={{
              __html: t("Learning.tool1.description"),
            }}
          />
        </Box>
        <Box mb={16}>
          <Heading
            fontSize="3xl"
            textTransform={"uppercase"}
            mb={8}
            fontWeight="bold"
          >
            {t("Learning.tool2.title")}
          </Heading>
          <Text
            fontSize="lg"
            dangerouslySetInnerHTML={{
              __html: t("Learning.tool2.description"),
            }}
          />
        </Box>
        <Box mb={16}>
          <Heading
            fontSize="3xl"
            textTransform={"uppercase"}
            mb={8}
            fontWeight="bold"
          >
            {t("Learning.tool3.title")}
          </Heading>
          <Text
            fontSize="lg"
            dangerouslySetInnerHTML={{
              __html: t("Learning.tool3.description"),
            }}
          />
        </Box>
        <Heading fontSize="lg">{t("andMore")}</Heading>
      </CanaryContainer>
    </Container>
  );
};

const AssessmentsSection = () => {
  const t = useTranslations();
  return (
    <Container
      maxW="container.xl"
      py={{ base: 8, md: 24 }}
      px={{ base: 8, md: 24 }}
      id="AssessmentsSection"
    >
      <SectionHeader
        title={t("Learning.assessments.title")}
        description={t("Learning.assessments.description")}
        image={clarifayoAssessments}
      />
      <CanaryContainer>
        <Box mb={16}>
          <Heading
            fontSize="3xl"
            textTransform={"uppercase"}
            mb={8}
            fontWeight="bold"
          >
            {t("Learning.assessment1.title")}
          </Heading>
          <Text
            fontSize="lg"
            dangerouslySetInnerHTML={{
              __html: t("Learning.assessment1.description"),
            }}
          />
        </Box>
        <Box mb={16}>
          <Heading
            fontSize="3xl"
            textTransform={"uppercase"}
            mb={8}
            fontWeight="bold"
          >
            {t("Learning.assessment2.title")}
          </Heading>
          <Text
            fontSize="lg"
            dangerouslySetInnerHTML={{
              __html: t("Learning.assessment2.description"),
            }}
          />
        </Box>
        <Box mb={16}>
          <Heading
            fontSize="3xl"
            textTransform={"uppercase"}
            mb={8}
            fontWeight="bold"
          >
            {t("Learning.assessment3.title")}
          </Heading>
          <Text
            fontSize="lg"
            dangerouslySetInnerHTML={{
              __html: t("Learning.assessment3.description"),
            }}
          />
        </Box>
        <Heading fontSize="lg">{t("andMore")}</Heading>
      </CanaryContainer>
    </Container>
  );
};

export default function ToWhom() {
  return (
    <>
      <Head>
        <title>Clarifayo - maximize your Coaching Activity</title>
      </Head>
      <Box w="full" bgColor="brand.100">
        <Hero />
        <CoursesSection />
        <ToolsSection />
        <AssessmentsSection />
        <TestimonialsSection />
        <GetStarted />
        <Footer />
      </Box>
    </>
  );
}

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {},
  };
};
